import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

// ============================================================
// CARD DIMENSIONS
// Standard CR80 / bank-card size
// ============================================================

const CARD_WIDTH = "85.6mm";
const CARD_HEIGHT = "53.98mm";

// ============================================================
// ROYALTY LEVEL
// ============================================================

const getLevel = (customer) => {
  if (customer?.level) {
    return String(customer.level).toUpperCase();
  }

  const points = Number(
    customer?.reward_cycle_points ??
      customer?.total_points ??
      customer?.points ??
      0
  );

  if (points >= 1000) return "ELITE";
  if (points >= 600) return "VIP";
  if (points >= 300) return "GOLD";
  if (points >= 150) return "SILVER";
  if (points >= 71) return "BRONZE_PLUS";
  if (points >= 31) return "BRONZE";
  if (points >= 11) return "STARTER";

  return "NEW";
};

// ============================================================
// LEVEL COLORS
// ============================================================

const getLevelColors = (level) => {
  switch (level) {
    case "ELITE":
      return {
        background: "#F8F7F0",
        panel: "#F0EACD",
        accent: "#B08D20",
        secondary: "#D8C77A",
        border: "#C8A951",
        text: "#2F2A1D",
        muted: "#6B6250",
        badge: "#E9D88A",
        badgeText: "#3A3218",
        decoration: "#C8A951",
      };

    case "VIP":
      return {
        background: "#FBF7EE",
        panel: "#F3E5C5",
        accent: "#A66A12",
        secondary: "#D8B56A",
        border: "#C79742",
        text: "#3A2C19",
        muted: "#6D5B42",
        badge: "#E9C97B",
        badgeText: "#3D2A0C",
        decoration: "#C79742",
      };

    case "GOLD":
      return {
        background: "#FFFDF3",
        panel: "#F8EDBA",
        accent: "#A87C00",
        secondary: "#E5CF67",
        border: "#D1B54A",
        text: "#3F3514",
        muted: "#6F643F",
        badge: "#EBDD91",
        badgeText: "#42370C",
        decoration: "#D1B54A",
      };

    case "SILVER":
      return {
        background: "#F7F8FA",
        panel: "#E9EDF2",
        accent: "#64748B",
        secondary: "#CBD5E1",
        border: "#AEB8C5",
        text: "#263241",
        muted: "#64707F",
        badge: "#DDE3EA",
        badgeText: "#344152",
        decoration: "#AEB8C5",
      };

    case "BRONZE_PLUS":
      return {
        background: "#FBF7F3",
        panel: "#F0DED0",
        accent: "#9A5B2A",
        secondary: "#D5A27D",
        border: "#C58A62",
        text: "#3C2B21",
        muted: "#705B4E",
        badge: "#E4C1A8",
        badgeText: "#4A2C1A",
        decoration: "#C58A62",
      };

    case "BRONZE":
      return {
        background: "#FCF9F6",
        panel: "#F3E5D9",
        accent: "#95613D",
        secondary: "#D8B59B",
        border: "#C89B7D",
        text: "#3D3028",
        muted: "#725F54",
        badge: "#E6CABB",
        badgeText: "#493124",
        decoration: "#C89B7D",
      };

    case "STARTER":
      return {
        background: "#F7FAFE",
        panel: "#E2EDF9",
        accent: "#356A9A",
        secondary: "#AFCBE5",
        border: "#86AACA",
        text: "#243B53",
        muted: "#60778D",
        badge: "#C9DDF0",
        badgeText: "#244765",
        decoration: "#86AACA",
      };

    default:
      return {
        background: "#F8F9FC",
        panel: "#E8EBF5",
        accent: "#5B65A6",
        secondary: "#BFC6E2",
        border: "#9DA6D0",
        text: "#292F4A",
        muted: "#646B82",
        badge: "#D9DDF0",
        badgeText: "#30375D",
        decoration: "#9DA6D0",
      };
  }
};

// ============================================================
// REWARD
// ============================================================

const getRewardName = (customer) => {
  const reward =
    customer?.reward ??
    customer?.rewards_available ??
    null;

  if (!reward) {
    return "No reward available";
  }

  if (typeof reward === "string") {
    return reward;
  }

  if (Array.isArray(reward)) {
    if (reward.length === 0) {
      return "No reward available";
    }

    const lastReward = reward[reward.length - 1];

    if (typeof lastReward === "string") {
      return lastReward;
    }

    return (
      lastReward?.name ||
      lastReward?.reward_name ||
      "Reward available"
    );
  }

  return (
    reward?.name ||
    reward?.reward_name ||
    "Reward available"
  );
};

// ============================================================
// SAFE TEXT
// ============================================================

const safeText = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return String(value);
};

// ============================================================
// STYLES
// ============================================================

const styles = StyleSheet.create({
  page: {
    width: "210mm",
    height: "297mm",
    backgroundColor: "#FFFFFF",
    position: "relative",
  },

  // ==========================================================
  // CARD
  // ==========================================================

  card: {
    position: "absolute",

    width: CARD_WIDTH,
    height: CARD_HEIGHT,

    left: "50%",
    top: "50%",

    marginLeft: "-42.8mm",
    marginTop: "-26.99mm",

    borderRadius: 10,

    borderWidth: 0.8,

    overflow: "hidden",

    padding: 0,
  },

  // ==========================================================
  // DECORATION
  // ==========================================================

  decoration: {
    position: "absolute",

    width: "100%",
    height: "100%",

    left: 0,
    top: 0,
  },

  circleLarge: {
    position: "absolute",

    width: 105,
    height: 105,

    borderRadius: 105,

    right: -48,
    top: -50,
  },

  circleSmall: {
    position: "absolute",

    width: 70,
    height: 70,

    borderRadius: 70,

    left: -34,
    bottom: -35,
  },

  line: {
    position: "absolute",

    height: 0.8,

    width: 145,

    right: -35,
    top: 40,

    transform: "rotate(-24deg)",
  },

  // ==========================================================
  // TOP ACCENT
  // ==========================================================

  topAccent: {
    position: "absolute",

    left: 0,
    right: 0,
    top: 0,

    height: 2,
  },

  // ==========================================================
  // HEADER
  // ==========================================================

  header: {
    flexDirection: "row",

    justifyContent: "space-between",

    alignItems: "flex-start",

    zIndex: 10,

    paddingHorizontal: 10,
    paddingTop: 10,
  },

  brand: {
    flexDirection: "row",

    alignItems: "center",
  },

  logo: {
    width: 27,
    height: 27,

    borderRadius: 6,

    justifyContent: "center",
    alignItems: "center",

    marginRight: 6,

    borderWidth: 0.8,
  },

  logoText: {
    fontSize: 9,

    fontWeight: "bold",

    color: "#FFFFFF",
  },

  brandTitle: {
    fontSize: 7,

    fontWeight: "bold",
  },

  brandSubtitle: {
    fontSize: 4.3,

    marginTop: 1,
  },

  // ==========================================================
  // LEVEL
  // ==========================================================

  levelArea: {
    alignItems: "flex-end",
  },

  levelLabel: {
    fontSize: 4.2,

    letterSpacing: 0.8,
  },

  levelBadge: {
    paddingHorizontal: 7,

    paddingVertical: 3,

    borderRadius: 20,

    marginTop: 2,

    borderWidth: 0.5,
  },

  levelText: {
    fontSize: 5.8,

    fontWeight: "bold",

    letterSpacing: 0.6,
  },

  // ==========================================================
  // CHIP
  // ==========================================================

  chip: {
    position: "absolute",

    width: 29,
    height: 20,

    borderRadius: 4,

    right: 70,
    top: 38,

    backgroundColor: "#E2C86A",

    borderWidth: 0.5,
    borderColor: "#B69B38",

    zIndex: 10,
  },

  // ==========================================================
  // CUSTOMER
  //
  // FIX:
  // A reserved fixed-height area prevents long customer names
  // from extending into the website section.
  // ==========================================================

  customerArea: {
    position: "absolute",

    left: 10,
    top: 39,

    width: 105,
    height: 20,

    zIndex: 10,

    overflow: "hidden",
  },

  customerName: {
    fontSize: 10.5,

    fontWeight: "bold",

    width: 105,

    maxLines: 1,

    overflow: "hidden",
  },

  customerPhone: {
    fontSize: 6.3,

    marginTop: 2,

    width: 105,

    maxLines: 1,

    overflow: "hidden",
  },

  // ==========================================================
  // WEBSITE / ACCOUNT
  //
  // FIX:
  // Positioned below the reserved customer area.
  // ==========================================================

  websiteArea: {
    position: "absolute",

    left: 10,
    top: 61,

    width: 110,

    zIndex: 10,

    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 4,
    paddingRight: 4,

    borderWidth: 0.5,
    borderRadius: 3,

    alignItems: "center",
    justifyContent: "center",
  },

  websiteTitle: {
    fontSize: 3.8,

    fontWeight: "bold",

    letterSpacing: 0.4,

    textAlign: "center",
  },

  websiteUrl: {
    fontSize: 4.8,

    fontWeight: "bold",

    marginTop: 1,

    textAlign: "center",
  },

  // ==========================================================
  // LOWER AREA
  // ==========================================================

  lowerArea: {
    position: "absolute",

    left: 8,
    right: 8,
    bottom: 5,

    flexDirection: "row",

    justifyContent: "space-between",

    alignItems: "flex-end",

    zIndex: 10,
  },

  information: {
    flex: 1,

    paddingRight: 5,

    minWidth: 0,
  },

  informationRow: {
    flexDirection: "row",

    marginBottom: 4,
  },

  informationBlock: {
    marginRight: 11,
  },

  informationLabel: {
    fontSize: 3.8,

    letterSpacing: 0.4,
  },

  informationValue: {
    fontSize: 6.2,

    fontWeight: "bold",

    marginTop: 1,
  },

  rewardText: {
    fontSize: 4.3,

    maxWidth: 130,
  },

  // ==========================================================
  // FRONT QR
  // ==========================================================

  frontQrArea: {
    width: 72,

    alignItems: "center",

    justifyContent: "flex-end",

    flexShrink: 0,

    marginLeft: 4,
  },

  frontQrContainer: {
    width: 70,
    height: 70,

    backgroundColor: "#FFFFFF",

    borderRadius: 3,

    padding: 2,

    justifyContent: "center",

    alignItems: "center",

    borderWidth: 0.8,
    borderColor: "#C9CDD4",

    flexShrink: 0,
  },

  frontQr: {
    width: 66,
    height: 66,

    flexShrink: 0,

    objectFit: "contain",
  },

  frontQrLabel: {
    fontSize: 4.5,

    fontWeight: "bold",

    marginTop: 2,

    letterSpacing: 0.6,
  },

  qrMissing: {
    width: 66,
    height: 66,

    backgroundColor: "#F3F4F6",

    justifyContent: "center",

    alignItems: "center",

    flexShrink: 0,
  },

  qrMissingText: {
    fontSize: 4,

    color: "#6B7280",

    textAlign: "center",
  },

  // ==========================================================
  // BACK CONTENT
  // ==========================================================

  backContent: {
    position: "absolute",

    left: 0,
    top: 0,

    width: CARD_WIDTH,
    height: CARD_HEIGHT,

    zIndex: 10,
  },

  // ==========================================================
  // BACK QR
  // ==========================================================

  backQrContainer: {
    position: "absolute",

    width: "49mm",
    height: "49mm",

    left: "50%",
    top: "50%",

    marginLeft: "-24.5mm",
    marginTop: "-24.5mm",

    backgroundColor: "#FFFFFF",

    borderRadius: 1,

    padding: 1,

    justifyContent: "center",
    alignItems: "center",

    borderWidth: 0.6,
    borderColor: "#C9CDD4",

    flexShrink: 0,
  },

  backQr: {
    width: "47mm",
    height: "47mm",

    flexShrink: 0,

    objectFit: "contain",
  },

  backQrMissing: {
    width: "47mm",
    height: "47mm",

    backgroundColor: "#F3F4F6",

    justifyContent: "center",
    alignItems: "center",

    padding: 3,

    flexShrink: 0,
  },

  backQrMissingText: {
    fontSize: 5,

    color: "#6B7280",

    textAlign: "center",
  },

  // ==========================================================
  // BACK FOOTER
  // ==========================================================

  footer: {
    position: "absolute",

    left: 5,
    right: 5,
    bottom: 2,

    flexDirection: "row",

    justifyContent: "space-between",

    alignItems: "center",

    zIndex: 20,
  },

  footerText: {
    fontSize: 3,

    color: "#6B7280",

    maxLines: 1,

    overflow: "hidden",
  },
});

// ============================================================
// CARD DECORATION
// ============================================================

const CardDecoration = ({ colors }) => {
  return (
    <View style={styles.decoration}>
      <View
        style={[
          styles.topAccent,
          {
            backgroundColor: colors.accent,
          },
        ]}
      />

      <View
        style={[
          styles.circleLarge,
          {
            backgroundColor: colors.decoration,
            opacity: 0.09,
          },
        ]}
      />

      <View
        style={[
          styles.circleSmall,
          {
            backgroundColor: colors.decoration,
            opacity: 0.07,
          },
        ]}
      />

      <View
        style={[
          styles.line,
          {
            backgroundColor: colors.decoration,
            opacity: 0.18,
          },
        ]}
      />
    </View>
  );
};

// ============================================================
// LOGO
// ============================================================

const Logo = ({ colors }) => {
  return (
    <View
      style={[
        styles.logo,
        {
          backgroundColor: colors.accent,
          borderColor: colors.border,
        },
      ]}
    >
      <Text style={styles.logoText}>
        ZU
      </Text>
    </View>
  );
};

// ============================================================
// MAIN PDF
// ============================================================

const LoyaltyCardPDF = ({
  customer,
  qrDataUrl,
}) => {
  // ==========================================================
  // CUSTOMER VALIDATION
  // ==========================================================

  if (!customer) {
    return (
      <Document
        title="Zulu Royalty Card"
        author="Zulu Whiskey Shipping Company"
      >
        <Page
          size="A4"
          style={styles.page}
        >
          <Text>
            Customer information unavailable.
          </Text>
        </Page>
      </Document>
    );
  }

  // ==========================================================
  // POINTS
  // ==========================================================

  const points = Number(
    customer?.reward_cycle_points ??
      customer?.total_points ??
      customer?.points ??
      0
  );

  // ==========================================================
  // LEVEL
  // ==========================================================

  const level = getLevel(customer);

  const colors = getLevelColors(level);

  // ==========================================================
  // QR TOKEN
  // ==========================================================

  const qrToken = safeText(
    customer?.card_qr_token ||
      customer?.qr_token ||
      ""
  );

  // ==========================================================
  // QR IMAGE
  // ==========================================================

  const hasQRCode =
    Boolean(qrDataUrl) &&
    Boolean(qrToken);

  // ==========================================================
  // CUSTOMER NAME
  // ==========================================================

  const customerName = safeText(
    customer?.full_name,
    "Customer"
  );

  // ==========================================================
  // PHONE
  // ==========================================================

  const customerPhone = safeText(
    customer?.phone,
    "No phone number"
  );

  // ==========================================================
  // CARD NUMBER
  // ==========================================================

  const cardNumber =
    customer?.card_number
      ? safeText(customer.card_number)
      : customer?.id !== undefined &&
        customer?.id !== null
      ? `ZW-${String(customer.id).padStart(6, "0")}`
      : "ZW-000000";

  // ==========================================================
  // EXPIRY
  // ==========================================================

  const expiryDate = safeText(
    customer?.expiry_date,
    ""
  );

  // ==========================================================
  // REWARD
  // ==========================================================

  const rewardName = safeText(
    getRewardName(customer),
    "No reward available"
  );

  // ==========================================================
  // POINTS DISPLAY
  // ==========================================================

  const pointsDisplay = String(points);

  // ==========================================================
  // DOCUMENT
  // ==========================================================

  return (
    <Document
      title={`${customerName} Loyalty Card`}
      author="Zulu Whiskey Shipping Company"
      subject="Zulu Royalty Customer Card"
      creator="Zulu Whiskey Shipping Company"
    >
      {/* ======================================================
          FRONT
      ====================================================== */}

      <Page
        size="A4"
        style={styles.page}
      >
        <View
          style={[
            styles.card,
            {
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
        >
          <CardDecoration colors={colors} />

          {/* ==================================================
              HEADER
          ================================================== */}

          <View style={styles.header}>
            <View style={styles.brand}>
              <Logo colors={colors} />

              <View>
                <Text
                  style={[
                    styles.brandTitle,
                    {
                      color: colors.text,
                    },
                  ]}
                >
                  ZULU WHISKEY
                </Text>

                <Text
                  style={[
                    styles.brandSubtitle,
                    {
                      color: colors.muted,
                    },
                  ]}
                >
                  SHIPPING COMPANY
                </Text>
              </View>
            </View>

            <View style={styles.levelArea}>
              <Text
                style={[
                  styles.levelLabel,
                  {
                    color: colors.muted,
                  },
                ]}
              >
                ROYALTY LEVEL
              </Text>

              <View
                style={[
                  styles.levelBadge,
                  {
                    backgroundColor: colors.badge,
                    borderColor: colors.border,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.levelText,
                    {
                      color: colors.badgeText,
                    },
                  ]}
                >
                  {safeText(level)}
                </Text>
              </View>
            </View>
          </View>

          {/* ==================================================
              CHIP
          ================================================== */}

          <View style={styles.chip} />

          {/* ==================================================
              CUSTOMER
          ================================================== */}

          <View style={styles.customerArea}>
            <Text
              style={[
                styles.customerName,
                {
                  color: colors.text,
                },
              ]}
              maxLines={1}
            >
              {customerName}
            </Text>

            <Text
              style={[
                styles.customerPhone,
                {
                  color: colors.muted,
                },
              ]}
              maxLines={1}
            >
              {customerPhone}
            </Text>
          </View>

          {/* ==================================================
              WEBSITE / ACCOUNT
          ================================================== */}

          <View
            style={[
              styles.websiteArea,
              {
                backgroundColor: colors.panel,
                borderColor: colors.border,
              },
            ]}
          >
            <Text
              style={[
                styles.websiteTitle,
                {
                  color: colors.muted,
                },
              ]}
            >
              VISIT OR CHECK YOUR ACCOUNT
            </Text>

            <Text
              style={[
                styles.websiteUrl,
                {
                  color: colors.accent,
                },
              ]}
            >
              www.zuluwhiskeyshipping.com
            </Text>
          </View>

          {/* ==================================================
              LOWER INFORMATION
          ================================================== */}

          <View style={styles.lowerArea}>
            <View style={styles.information}>
              <View style={styles.informationRow}>
                <View style={styles.informationBlock}>
                  <Text
                    style={[
                      styles.informationLabel,
                      {
                        color: colors.muted,
                      },
                    ]}
                  >
                    CARD NUMBER
                  </Text>

                  <Text
                    style={[
                      styles.informationValue,
                      {
                        color: colors.text,
                      },
                    ]}
                  >
                    {cardNumber}
                  </Text>
                </View>

                <View style={styles.informationBlock}>
                  <Text
                    style={[
                      styles.informationLabel,
                      {
                        color: colors.muted,
                      },
                    ]}
                  >
                    POINTS
                  </Text>

                  <Text
                    style={[
                      styles.informationValue,
                      {
                        color: colors.text,
                      },
                    ]}
                  >
                    {pointsDisplay}
                  </Text>
                </View>

                {expiryDate ? (
                  <View style={styles.informationBlock}>
                    <Text
                      style={[
                        styles.informationLabel,
                        {
                          color: colors.muted,
                        },
                      ]}
                    >
                      VALID UNTIL
                    </Text>

                    <Text
                      style={[
                        styles.informationValue,
                        {
                          color: colors.text,
                        },
                      ]}
                    >
                      {expiryDate}
                    </Text>
                  </View>
                ) : null}
              </View>

              <Text
                style={[
                  styles.rewardText,
                  {
                    color: colors.muted,
                  },
                ]}
              >
                {`Reward: ${rewardName}`}
              </Text>
            </View>

            {/* ==================================================
                FRONT QR
            ================================================== */}

            <View style={styles.frontQrArea}>
              <View style={styles.frontQrContainer}>
                {hasQRCode ? (
                  <Image
                    src={qrDataUrl}
                    style={styles.frontQr}
                  />
                ) : (
                  <View style={styles.qrMissing}>
                    <Text style={styles.qrMissingText}>
                      QR unavailable
                    </Text>
                  </View>
                )}
              </View>

              <Text
                style={[
                  styles.frontQrLabel,
                  {
                    color: colors.accent,
                  },
                ]}
              >
                SCAN
              </Text>
            </View>
          </View>
        </View>
      </Page>

      {/* ======================================================
          BACK
      ====================================================== */}

      <Page
        size="A4"
        style={styles.page}
      >
        <View
          style={[
            styles.card,
            {
              backgroundColor: colors.background,
              borderColor: colors.border,
            },
          ]}
        >
          <CardDecoration colors={colors} />

          <View style={styles.backContent}>
            <View style={styles.backQrContainer}>
              {hasQRCode ? (
                <Image
                  src={qrDataUrl}
                  style={styles.backQr}
                />
              ) : (
                <View style={styles.backQrMissing}>
                  <Text style={styles.backQrMissingText}>
                    QR CODE UNAVAILABLE
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* ==================================================
              BACK FOOTER
          ================================================== */}

          <View style={styles.footer}>
            <Text
              style={[
                styles.footerText,
                {
                  color: colors.muted,
                },
              ]}
              maxLines={1}
            >
              {customerName}
            </Text>

            <Text
              style={[
                styles.footerText,
                {
                  color: colors.muted,
                },
              ]}
              maxLines={1}
            >
              {cardNumber}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default LoyaltyCardPDF;

